import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { format } from "date-fns";
import { ChevronDown } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../providers/AuthProvider";
import { Alert, AlertDescription, AlertTitle } from "../../app/components/ui/alert";
import { Badge } from "../../app/components/ui/badge";
import { Button } from "../../app/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../app/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../app/components/ui/collapsible";
import { Progress } from "../../app/components/ui/progress";
import { ProfileDataSections } from "./profileDataPresentation";

type Detail = Awaited<ReturnType<typeof api.admin.getBusinessProfileDetail>>;

export function BusinessProfileDetailPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const params = useParams();
  const businessId = params.businessId ?? "";

  const [row, setRow] = useState<Detail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token || !businessId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.admin.getBusinessProfileDetail({ token, businessId });
        if (!cancelled) setRow(res);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Failed to load business profile");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, businessId]);

  const prettyJson = useMemo(() => {
    if (!row) return "";
    return JSON.stringify(row.profile_data ?? {}, null, 2);
  }, [row]);

  const profileDataAsSections = useMemo(() => {
    if (!row?.profile_data || typeof row.profile_data !== "object") return null;
    return row.profile_data as Record<string, Record<string, unknown>>;
  }, [row]);

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-semibold text-foreground mb-1">Business profile</h1>
        <p className="text-muted-foreground">View onboarding completion and filled fields.</p>
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertTitle>Something went wrong</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle>{row?.business_name ?? (loading ? "Loading…" : "Business")}</CardTitle>
          <CardDescription>
            Business ID: <span className="font-mono text-xs">{businessId}</span>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {loading ? (
            <div className="text-muted-foreground">Loading…</div>
          ) : row ? (
            <>
              <div className="flex flex-wrap items-center gap-3">
                <Badge variant={row.is_complete ? "default" : "secondary"}>
                  {row.is_complete ? "complete" : "incomplete"}
                </Badge>
                <Badge variant="outline">{row.business_status}</Badge>
                {row.store_type ? <Badge variant="outline">{row.store_type}</Badge> : null}
                {row.updated_after_verification ? (
                  <Badge className="bg-amber-100 text-amber-900 border-amber-200 hover:bg-amber-100">
                    Profile updated since verification
                  </Badge>
                ) : null}
              </div>

              {row.last_updated_at ? (
                <p className="text-sm text-muted-foreground">
                  Last profile update: {format(new Date(row.last_updated_at), "PPpp")}
                  {row.last_updated_by_email ? (
                    <span className="ml-1">({row.last_updated_by_email})</span>
                  ) : null}
                </p>
              ) : null}

              <div className="space-y-2">
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>Completion</span>
                  <span>{row.completion_percentage}%</span>
                </div>
                <Progress value={row.completion_percentage} className="h-2" />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Owner</CardTitle>
                    <CardDescription>Account linked to this business</CardDescription>
                  </CardHeader>
                  <CardContent className="text-sm">
                    <div className="font-medium">{row.owner_name ?? "—"}</div>
                    <div className="text-muted-foreground">{row.owner_email ?? "—"}</div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Completed steps</CardTitle>
                    <CardDescription>Saved and validated steps</CardDescription>
                  </CardHeader>
                  <CardContent className="flex flex-wrap gap-2">
                    {(row.completed_steps ?? []).length ? (
                      row.completed_steps.map((s) => (
                        <Badge key={s} variant="secondary">
                          {s}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-sm text-muted-foreground">None yet</span>
                    )}
                  </CardContent>
                </Card>
              </div>

              <div className="space-y-2">
                <div>
                  <h2 className="text-lg font-semibold text-foreground">Onboarding details</h2>
                  <p className="text-sm text-muted-foreground">
                    Values grouped by step. Use “Raw JSON” if you need to copy the exact payload.
                  </p>
                </div>
                <ProfileDataSections profileData={profileDataAsSections} />
              </div>

              <Collapsible className="rounded-lg border border-border bg-card">
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left text-sm font-medium hover:bg-muted/50 rounded-lg"
                  >
                    <span>Raw JSON (copy / audit)</span>
                    <ChevronDown className="h-4 w-4 shrink-0 opacity-60" />
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="border-t border-border px-4 pb-4">
                    <pre className="text-xs bg-muted rounded-lg p-4 overflow-auto max-h-[480px] mt-3">
                      {prettyJson}
                    </pre>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            </>
          ) : (
            <div className="text-muted-foreground">Not found.</div>
          )}

          <div className="flex justify-end">
            <Button type="button" variant="outline" onClick={() => navigate(-1)}>
              Back
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

